import { Box, type SxProps, type Theme } from "@mui/material";
import { useEffect, useLayoutEffect, useRef } from "react";
import { prefersReducedMotion } from "../motion/reducedMotion";
import {
  pickPosterWidth,
  pickRenditionWidth,
  readRenditionContext,
} from "./pickRendition";
import {
  siteVideoEntry,
  siteVideoPosterUrl,
  siteVideoUrl,
  type SiteVideoName,
} from "./siteMedia";

/** 아래쪽 영상은 화면 이만큼 앞에서 받기 시작한다 */
const PRELOAD_ROOT_MARGIN = "600px";
/** 이만큼 보이면 재생한다 */
const PLAY_THRESHOLD = 0.15;

export interface SiteVideoProps {
  name: SiteVideoName;
  /** 첫 화면 영상은 바로, 아래 영상은 화면 가까이 와야 받는다(포스터 포함) */
  loading: "eager" | "lazy";
  /** 내용이 있는 영상의 설명. 장식이면 비우고 `aria-hidden` 으로 숨긴다 */
  ariaLabel?: string;
  /** 화면에 들어올 때마다 처음부터 재생(장면 녹화처럼 처음부터 봐야 하는 영상) */
  restartOnEnter?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * 사이트 영상(site-media-delivery): 기기에 맞는 렌디션 하나만 받고, 소리 없이 보이는 동안만 반복 재생한다.
 * 받기 전에는 포스터의 흐린 미리보기가 바탕을 채운다. 움직임 줄이기면 자동 재생하지 않고 포스터만 둔다.
 */
export function SiteVideo({
  name,
  loading,
  ariaLabel,
  restartOnEnter = false,
  sx,
}: SiteVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const entry = siteVideoEntry(name);
  const reducedMotionRef = useRef(false);

  useLayoutEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    reducedMotionRef.current = prefersReducedMotion();
    const displayWidth =
      video.clientWidth || video.parentElement?.clientWidth || 0;
    const width = pickRenditionWidth(
      entry.v,
      readRenditionContext(displayWidth)
    );
    const posterWidth = pickPosterWidth(entry.p, width);
    video.muted = true;
    video.dataset.rendition = String(width);
    // 아래쪽 영상은 포스터도 영상과 함께 화면 가까이 와서 받는다 — 그 전에는 흐린 미리보기 바탕이 채운다
    const attachSource = () => {
      if (video.getAttribute("src")) return;
      if (posterWidth !== undefined)
        video.poster = siteVideoPosterUrl(name, posterWidth);
      video.preload = reducedMotionRef.current ? "metadata" : "auto";
      video.src = siteVideoUrl(name, width);
    };
    if (loading === "eager") {
      attachSource();
      return;
    }
    const nearObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((observed) => observed.isIntersecting)) return;
        nearObserver.disconnect();
        attachSource();
      },
      { rootMargin: PRELOAD_ROOT_MARGIN }
    );
    nearObserver.observe(video);
    return () => nearObserver.disconnect();
  }, [entry, loading, name]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const visibilityObserver = new IntersectionObserver(
      ([observed]) => {
        if (observed.isIntersecting && !reducedMotionRef.current) {
          if (restartOnEnter) {
            try {
              video.currentTime = 0;
            } catch {
              // 메타데이터 전이면 처음부터 재생된다
            }
          }
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: PLAY_THRESHOLD }
    );
    visibilityObserver.observe(video);
    return () => visibilityObserver.disconnect();
  }, [restartOnEnter]);

  return (
    <Box
      component="video"
      ref={videoRef}
      muted
      loop
      playsInline
      preload="none"
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : true}
      sx={[
        entry.q
          ? { backgroundImage: `url(${entry.q})`, backgroundSize: "cover" }
          : {},
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );
}
